import React, { useEffect, useState } from 'react';
import { PhoneIcon, UserIcon, TagIcon, TrendingUpIcon, AlertTriangleIcon, SearchIcon, CheckCircleIcon, ChevronRightIcon, ChevronLeftIcon, SaveIcon, PackageIcon, PlusIcon, XIcon, MapPinIcon, ClockIcon, DollarSignIcon, TrashIcon, HistoryIcon, CalendarIcon, EyeIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Badge } from '../components/ui/Badge';
import { Modal, ModalBody } from '../components/ui/Modal';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { Client, Product } from '../types';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type TabType = 'speech' | 'historial';
type FlowType = 'ofertas' | 'alta_rotacion' | 'vencimientos' | 'validar_riesgo' | '';
type CallResult = 'venta_realizada' | 'promesa_compra' | 'promesa_cobro' | 'no_interesado' | 'seguimiento' | 'no_contesto' | 'ocupado' | '';
interface SelectedProduct {
  productoId: string;
  nombre: string;
  sku: string;
  precio: number;
}
interface VencidoCliente {
  productoId: string;
  nombre: string;
  sku: string;
  cantidad: number;
  notas: string;
}
export function CallCenterPage() {
  const {
    user
  } = useAuth();
  const {
    clients
  } = useApp();
  const [activeTab, setActiveTab] = useState<TabType>('speech');
  const [callsHistory, setCallsHistory] = useState<any[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedCall, setSelectedCall] = useState<any | null>(null);
  // Wizard State
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Call Data State
  const [selectedClientId, setSelectedClientId] = useState('');
  const [flow, setFlow] = useState<FlowType>('');
  const [vencidosCliente, setVencidosCliente] = useState<VencidoCliente[]>([]);
  const [offeredProducts, setOfferedProducts] = useState<SelectedProduct[]>([]);
  const [interestedProducts, setInterestedProducts] = useState<SelectedProduct[]>([]);
  const [callResult, setCallResult] = useState<CallResult>('');
  const [notes, setNotes] = useState('');
  // Inventory State
  const [inventory, setInventory] = useState<Product[]>([]);
  const [promotions, setPromotions] = useState<string[]>([]);
  const [topProducts, setTopProducts] = useState<Record<string, number>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  // Vencidos Form State
  const [vencidoSearch, setVencidoSearch] = useState('');
  const [vencidoQty, setVencidoQty] = useState(1);
  const [vencidoNotes, setVencidoNotes] = useState('');
  const selectedClient = clients.find(c => c.id === selectedClientId);
  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('call_center_calls').select('*').order('fecha', {
        ascending: false
      }).limit(100);
      if (error) throw error;
      if (data) setCallsHistory(data);
    } catch (err) {
      console.error('Error fetching call history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };
  useEffect(() => {
    if (activeTab === 'historial') {
      fetchHistory();
    }
  }, [activeTab]);
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingInventory(true);
      try {
        // EMERGENCY: Single query with limit instead of while(hasMore) loop
        const {
          data,
          error
        } = await supabaseAdmin.from('products').select('*').order('nombre').limit(2000);
        if (!error && data) {
          const allProducts: Product[] = data.map((p: any) => ({
            id: p.id,
            nombre: p.nombre,
            sku: p.sku,
            stock: p.stock,
            stockMinimo: p.stock_minimo ?? 0,
            precio: p.precio,
            subTotal: p.sub_total || 0,
            itbis: p.itbis || 0,
            proveedor: p.proveedor,
            fechaVencimiento: p.fecha_vencimiento || null,
            estado: p.estado
          }));
          setInventory(allProducts);
          // Load active promotions
          const {
            data: promoData
          } = await supabaseAdmin.from('product_promotions').select('product_id').eq('activo', true);
          if (promoData) {
            setPromotions(promoData.map(p => p.product_id));
          }
          // Load top products (alta rotacion)
          const {
            data: orderLines
          } = await supabaseAdmin.from('order_lines').select('producto_id, cantidad');
          if (orderLines) {
            const counts: Record<string, number> = {};
            orderLines.forEach(line => {
              if (line.producto_id) {
                counts[line.producto_id] = (counts[line.producto_id] || 0) + line.cantidad;
              }
            });
            setTopProducts(counts);
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setIsLoadingInventory(false);
      }
    };
    loadData();
  }, []);
  const handleNext = () => {
    if (currentStep === 2 && flow !== 'validar_riesgo') {
      setCurrentStep(4); // Skip step 3 if not validating risk
    } else if (currentStep < 7) {
      setCurrentStep(prev => prev + 1 as Step);
    }
  };
  const handleBack = () => {
    if (currentStep === 4 && flow !== 'validar_riesgo') {
      setCurrentStep(2); // Go back to 2 if we skipped 3
    } else if (currentStep > 1) {
      setCurrentStep(prev => prev - 1 as Step);
    }
  };
  const resetForm = () => {
    setCurrentStep(1);
    setSelectedClientId('');
    setFlow('');
    setVencidosCliente([]);
    setOfferedProducts([]);
    setInterestedProducts([]);
    setCallResult('');
    setNotes('');
    setSubmitSuccess(false);
  };
  const handleSaveCall = async () => {
    if (!selectedClient || !user || !flow || !callResult) return;
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabaseAdmin.from('call_center_calls').insert({
        agente_id: user.id,
        agente_nombre: user.nombre,
        cliente_id: selectedClient.id,
        cliente_nombre: selectedClient.nombre,
        cliente_telefono: selectedClient.telefono,
        cliente_zona: selectedClient.zona,
        flujo_seleccionado: flow,
        productos_ofrecidos: offeredProducts,
        productos_interes: interestedProducts,
        productos_vencidos_cliente: vencidosCliente,
        tiene_productos_vencidos: vencidosCliente.length > 0,
        resultado: callResult,
        notas: notes
      });
      if (error) throw error;
      setSubmitSuccess(true);
    } catch (err) {
      console.error('Error saving call:', err);
      alert('Error al guardar el registro de la llamada.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleOfferedProduct = (product: Product) => {
    setOfferedProducts(prev => {
      const exists = prev.find(p => p.productoId === product.id);
      if (exists) return prev.filter(p => p.productoId !== product.id);
      return [...prev, {
        productoId: product.id,
        nombre: product.nombre,
        sku: product.sku,
        precio: product.precio
      }];
    });
  };
  const toggleInterestedProduct = (product: SelectedProduct) => {
    setInterestedProducts(prev => {
      const exists = prev.find(p => p.productoId === product.productoId);
      if (exists) return prev.filter(p => p.productoId !== product.productoId);
      return [...prev, product];
    });
  };
  const addVencidoCliente = (product: Product) => {
    setVencidosCliente(prev => [...prev, {
      productoId: product.id,
      nombre: product.nombre,
      sku: product.sku,
      cantidad: vencidoQty,
      notas: vencidoNotes
    }]);
    setVencidoSearch('');
    setVencidoQty(1);
    setVencidoNotes('');
  };
  const removeVencidoCliente = (index: number) => {
    setVencidosCliente(prev => prev.filter((_, i) => i !== index));
  };
  // Filter inventory for search and flow
  const filteredInventory = inventory.filter(p => p.nombre.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase())).sort((a, b) => {
    if (flow === 'ofertas') {
      const aPromo = promotions.includes(a.id);
      const bPromo = promotions.includes(b.id);
      if (aPromo && !bPromo) return -1;
      if (!aPromo && bPromo) return 1;
    } else if (flow === 'alta_rotacion') {
      const aCount = topProducts[a.id] || 0;
      const bCount = topProducts[b.id] || 0;
      if (aCount !== bCount) return bCount - aCount;
    } else if (flow === 'vencimientos') {
      // Sort by longest expiration date first (nulls last)
      if (a.fechaVencimiento && b.fechaVencimiento) {
        return new Date(b.fechaVencimiento).getTime() - new Date(a.fechaVencimiento).getTime();
      }
      if (a.fechaVencimiento) return -1;
      if (b.fechaVencimiento) return 1;
    }
    return a.nombre.localeCompare(b.nombre);
  }).slice(0, 15);
  const filteredVencidoSearch = inventory.filter(p => p.nombre.toLowerCase().includes(vencidoSearch.toLowerCase()) || p.sku.toLowerCase().includes(vencidoSearch.toLowerCase())).slice(0, 5);
  if (submitSuccess) {
    return <div className="space-y-6" data-id="element-554" data-mp="2blr74tx9mctu">
        <PageHeader title="Call Center" subtitle="Speech interactivo y gestión telefónica" icon={<PhoneIcon className="w-6 h-6" data-id="element-556" data-mp="21po5dp6bsg7f" />} data-id="element-555" data-mp="1wqcomg5ylapu" />
        
        <Card className="text-center py-16 animate-fade-in" data-id="element-557" data-mp="21p61c6df8xoy">
          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-558" data-mp="1m0llvgvafb8">
            <CheckCircleIcon className="w-10 h-10" data-id="element-559" data-mp="6my1u2ibgza3" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-560" data-mp="3nu9zauhf4ula">
            ¡Llamada Registrada!
          </h2>
          <p className="text-slate-500 mb-8 max-w-md mx-auto" data-id="element-561" data-mp="3stmnpjnxu9pz">
            El registro de la llamada con {selectedClient?.nombre} se ha
            guardado exitosamente en el sistema.
          </p>
          <Button onClick={resetForm} size="lg" data-id="element-562" data-mp="patd9fqgi101">
            Iniciar Nueva Llamada
          </Button>
        </Card>
      </div>;
  }
  return <div className="space-y-6 pb-20" data-id="element-563" data-mp="akvrf6g01ne0">
      <PageHeader title="Call Center" subtitle="Speech interactivo y gestión telefónica" icon={<PhoneIcon className="w-6 h-6" data-id="element-565" data-mp="13bvzbshgbbay" />} data-id="element-564" data-mp="fljghmko3u5l" />
      

      <div className="flex space-x-1 bg-slate-100 p-1 rounded-xl w-fit mb-6" data-id="element-566" data-mp="13a2sac2xep2f">
        <button onClick={() => setActiveTab('speech')} className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'speech' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'}`} data-id="element-567" data-mp="183dycazwz1ta">
          
          <PhoneIcon className="w-4 h-4 mr-2" data-id="element-568" data-mp="32j2pdet9o8bo" />
          Speech Interactivo
        </button>
        <button onClick={() => setActiveTab('historial')} className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'historial' ? 'bg-white text-indigo-700 shadow-sm' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'}`} data-id="element-569" data-mp="2xiy5det5gvgp">
          
          <HistoryIcon className="w-4 h-4 mr-2" data-id="element-570" data-mp="2iubrlhj68brl" />
          Historial de Llamadas
        </button>
      </div>

      {activeTab === 'historial' ? <Card data-id="element-571" data-mp="2es6zin8dfn72">
          <CardHeader title="Registro de Llamadas" data-id="element-572" data-mp="2erotwzztjwmh" />
          <div className="overflow-x-auto" data-id="element-573" data-mp="3u97e1po27hip">
            <table className="w-full text-sm text-left" data-id="element-574" data-mp="3u7xdmttqhx8w">
              <thead className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200" data-id="element-575" data-mp="3q8oo615q8lj5">
                <tr data-id="element-576" data-mp="2mxr8zqwslzi4">
                  <th className="px-4 py-3 font-medium" data-id="element-577" data-mp="2mw6gs7xyasnf">Fecha</th>
                  <th className="px-4 py-3 font-medium" data-id="element-578" data-mp="2rw2iu1n38a0c">Agente</th>
                  <th className="px-4 py-3 font-medium" data-id="element-579" data-mp="2ryf86zv6dmnf">Cliente</th>
                  <th className="px-4 py-3 font-medium" data-id="element-580" data-mp="2wwqjzg51678a">Flujo</th>
                  <th className="px-4 py-3 font-medium" data-id="element-581" data-mp="30xitmahtee53">Resultado</th>
                  <th className="px-4 py-3 font-medium" data-id="element-582" data-mp="30uo9kk2pqvc7">Detalles</th>
                  <th className="px-4 py-3 font-medium text-center" data-id="element-583" data-mp="248b6wz5glyzl">Ver</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-584" data-mp="29630rypnzv6f">
                {isLoadingHistory ? <tr data-id="element-585" data-mp="2d5v9qciz03cb">
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-id="element-586" data-mp="2d5br0tlhxax8">
                  
                      <div className="flex justify-center mb-2" data-id="element-587" data-mp="2hzzin1bsqlg1">
                        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-588" data-mp="1lfqsunuf5yfu" />
                      </div>
                      Cargando historial...
                    </td>
                  </tr> : callsHistory.length === 0 ? <tr data-id="element-589" data-mp="1czhr224c945o">
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-id="element-590" data-mp="1d0b0jauom7ri">
                  
                      No hay registros de llamadas aún.
                    </td>
                  </tr> : callsHistory.map(call => <tr key={call.id} className="hover:bg-slate-50" data-id="element-591" data-mp="13xs2d41ucjbl">
                      <td className="px-4 py-3 whitespace-nowrap" data-id="element-592" data-mp="z004zuifg500">
                        <div className="flex items-center text-slate-900" data-id="element-593" data-mp="z1b8rubvhcr4">
                          <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" data-id="element-594" data-mp="u7file1uou6i" />
                          {format(new Date(call.fecha), 'd MMM yyyy, h:mm a', {
                    locale: es
                  })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-900 font-medium" data-id="element-595" data-mp="g7xocj5scopr">
                        {call.agente_nombre}
                      </td>
                      <td className="px-4 py-3" data-id="element-596" data-mp="bc8lo6ygyrp8">
                        <p className="text-slate-900 font-medium" data-id="element-597" data-mp="2bd6snwzs9qi4">
                          {call.cliente_nombre}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-598" data-mp="2gd1irybqic24">
                          {call.cliente_zona}
                        </p>
                      </td>
                      <td className="px-4 py-3" data-id="element-599" data-mp="26k46i9xc5jby">
                        <Badge variant="secondary" className="capitalize" data-id="element-600" data-mp="1slnrzwt8eoj9">
                          {call.flujo_seleccionado.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" data-id="element-601" data-mp="1ingexp22cqmj">
                        <Badge variant={call.resultado === 'venta_realizada' ? 'success' : call.resultado === 'promesa_compra' || call.resultado === 'promesa_cobro' ? 'info' : call.resultado === 'seguimiento' ? 'warning' : 'danger'} data-id="element-602" data-mp="1ip16d5t2ztg6">
                    
                          {call.resultado.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-4 py-3" data-id="element-603" data-mp="16cfn68kk1sif">
                        <div className="text-xs space-y-1" data-id="element-604" data-mp="16b4k6ayxoosd">
                          {call.productos_interes?.length > 0 && <p className="text-emerald-600 font-medium" data-id="element-605" data-mp="11c1dyn77wcit">
                              {call.productos_interes.length} prod. interesaron
                            </p>}
                          {call.tiene_productos_vencidos && <p className="text-rose-600 font-medium flex items-center" data-id="element-606" data-mp="1p3q8ma7ihd2x">
                              <AlertTriangleIcon className="w-3 h-3 mr-1" data-id="element-607" data-mp="1p5jirzhc2ffo" />
                              Reportó vencidos
                            </p>}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-608" data-mp="1u2tetxz89pch">
                        <button onClick={() => setSelectedCall(call)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Ver detalle" data-id="element-609" data-mp="1u44ilxsoax3l">
                    
                          <EyeIcon className="w-4 h-4" data-id="element-610" data-mp="3vvhmqkmth0yl" />
                        </button>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </Card> : <>
          {/* Progress Bar */}
          <div className="mb-6 sm:mb-8" data-id="element-611" data-mp="tgcdh6tbf969">
            <div className="flex items-center justify-between relative" data-id="element-612" data-mp="ydchzdaio3v9">
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 sm:h-1 bg-slate-200 rounded-full -z-10" data-id="element-613" data-mp="13cfgeevzrbu9"></div>
              <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 sm:h-1 bg-indigo-600 rounded-full -z-10 transition-all duration-300" style={{
            width: `${(currentStep - 1) / 6 * 100}%`
          }} data-id="element-614" data-mp="18aqqmqq77od0">
            </div>

              {[1, 2, 3, 4, 5, 6, 7].map(step => <div key={step} className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-sm font-bold border-2 transition-colors ${step < currentStep ? 'bg-indigo-600 border-indigo-600 text-white' : step === currentStep ? 'bg-white border-indigo-600 text-indigo-600' : 'bg-white border-slate-300 text-slate-400'}`} data-id="element-615" data-mp="1m3q30juop2gc">
              
                  {step < currentStep ? <CheckCircleIcon className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-616" data-mp="3bklpwlun3ub8" /> : step}
                </div>)}
            </div>
            <div className="flex justify-between mt-1.5 sm:mt-2 text-[10px] sm:text-xs font-medium text-slate-500 px-1" data-id="element-617" data-mp="36kftksdfdjmt">
              <span data-id="element-618" data-mp="31npby0lz2fza">Apertura</span>
              <span data-id="element-619" data-mp="31oindevvltnr">Cierre</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-620" data-mp="33z8hmo16njy">
            {/* Left Column: Script & Guidance */}
            <div className="lg:col-span-1 space-y-6" data-id="element-621" data-mp="3ucwkztj1ynys">
              <Card className="bg-indigo-50 border-indigo-100 sticky top-6" data-id="element-622" data-mp="3pjbne2ldfw04">
                <CardHeader title="Guía de Llamada" data-id="element-623" data-mp="3kl9z939396hs" />
                <div className="mt-4 space-y-4" data-id="element-624" data-mp="3kkqjnt6wuu6x">
                  {currentStep === 1 && <div className="animate-fade-in" data-id="element-625" data-mp="lw1r33k8gdwu">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-626" data-mp="h4a0it12u86p">
                        Lee el siguiente texto:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-627" data-mp="3llb9b8aazuh8">
                        "Hola, ¿hablo con el encargado? Le habla{' '}
                        <span className="font-bold text-indigo-700 not-italic" data-id="element-628" data-mp="3cmhixznkg0c4">
                          {user?.nombre || 'su servidor'}
                        </span>{' '}
                        de Miladys Rodríguez y Asociados. Le llamo rápidamente
                        porque estamos viendo como ayudar a farmacias como la
                        suya a comprar mejor y mejorar la rotación de sus
                        productos."
                      </div>
                    </div>}

                  {currentStep === 2 && <div className="animate-fade-in" data-id="element-629" data-mp="441tphuly3b9">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-630" data-mp="2jw7sqqiyt49w">
                        Si responde que SÍ:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-631" data-mp="2ox59cyobtq9a">
                        "Perfecto, gracias. Para ayudarle mejor, le hago una
                        consulta rápida:
                        <br data-id="element-632" data-mp="2qcr66hiv71nj" />
                        <br data-id="element-633" data-mp="2qby6u1m8jeff" />
                        <strong data-id="element-634" data-mp="2qc7yruqubvnr">¿Le interesa más ahora mismo:</strong>
                        <br data-id="element-635" data-mp="2lezglu5ozrmi" />
                        • ver productos en oferta,
                        <br data-id="element-636" data-mp="1xnu0rezr4zc7" />
                        • productos que se están vendiendo rápido,
                        <br data-id="element-637" data-mp="1tna226zj5nny" />
                        • revisar opciones para evitar pérdidas por vencimiento,
                        <br data-id="element-638" data-mp="27eet4kou17mo" />• o validar productos que usted ya tenga en
                        riesgo?"
                      </div>
                    </div>}

                  {currentStep === 3 && <div className="animate-fade-in" data-id="element-639" data-mp="1exkotde5xt7b">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-640" data-mp="1ew17rkkone4h">
                        Registro de Vencimientos:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-641" data-mp="6e1pzzad2pwm">
                        "Entiendo.{' '}
                        <strong data-id="element-642" data-mp="1dniqjgl3jzn">
                          ¿Cuáles productos tiene en esa condición?
                        </strong>
                        "
                        <br data-id="element-643" data-mp="bcwad5nz8miu" />
                        <br data-id="element-644" data-mp="bbl85aa6jmtu" />
                        <span className="text-xs text-rose-600 font-bold not-italic block mt-2" data-id="element-645" data-mp="p4t0j4634mcl">
                          🔴 IMPORTANTE: Solo registrar, NO ofrecer devolución
                          en esta llamada.
                        </span>
                        <br data-id="element-646" data-mp="z2qnzpg4ef45" />
                        "Perfecto, tomo nota de esos productos. Eso le puede
                        estar generando pérdidas si no se mueven a tiempo. En
                        base a eso, le puedo ayudar con opciones que se vendan
                        más rápido o complementen esos productos."
                      </div>
                    </div>}

                  {currentStep === 4 && <div className="animate-fade-in" data-id="element-647" data-mp="1be9sre8ig62v">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-648" data-mp="1gd4d4km4m2p3">
                        Presentar Opciones:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-649" data-mp="11nex5j9dju4r">
                        "Le comento, tengo algunas opciones que le pueden
                        funcionar:"
                        <br data-id="element-650" data-mp="xltm83gehpx7" />
                        <br data-id="element-651" data-mp="xn4ofyu76pm7" />
                        <ul className="list-disc pl-4 space-y-1 mt-2" data-id="element-652" data-mp="xnegdrysz6uj">
                          {offeredProducts.length === 0 ? <li className="text-slate-400" data-id="element-653" data-mp="iqnhrkwydz5z">
                              Selecciona productos a la derecha...
                            </li> : offeredProducts.map(p => <li key={p.productoId} data-id="element-654" data-mp="2d2llj7chu2uh">
                                <strong data-id="element-655" data-mp="2d10v9tx7p62f">{p.nombre}</strong>
                              </li>)}
                        </ul>
                      </div>
                    </div>}

                  {currentStep === 5 && <div className="animate-fade-in" data-id="element-656" data-mp="2giolxl6i5w9d">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-657" data-mp="2bkmyyp5yhcsm">
                        Medir Interés:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-658" data-mp="26oo8mw12hkpk">
                        "<strong data-id="element-659" data-mp="2zazikxakcxz1">¿Cuál de estos le interesa más?</strong>
                        <br data-id="element-660" data-mp="2zdd86qmh5hz7" />o ¿Prefiere que le recomiende el que más salida
                        está teniendo en su zona?"
                      </div>
                    </div>}

                  {currentStep === 6 && <div className="animate-fade-in" data-id="element-661" data-mp="2pjd0cgzt5yms">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-662" data-mp="2lit37df8iv0n">
                        Ajuste Dinámico (Si duda):
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-663" data-mp="3db1v0403qz6w">
                        "Puedo ajustarle opciones:
                        <br data-id="element-664" data-mp="zu8j24vpaz1l" />
                        • más económicas
                        <br data-id="element-665" data-mp="14siiwssdentj" />
                        • con mayor ganancia
                        <br data-id="element-666" data-mp="18skhuu6q1354" />
                        • de más rotación"
                        <br data-id="element-667" data-mp="18vf0qha36fwf" />
                        <br data-id="element-668" data-mp="1dsos42h1d1mw" />
                        <span className="text-xs text-indigo-600 font-bold not-italic block" data-id="element-669" data-mp="1dtqdp0voh6iv">
                          Vuelve al paso 4 si necesitas buscar otros productos.
                        </span>
                      </div>
                    </div>}

                  {currentStep === 7 && <div className="animate-fade-in" data-id="element-670" data-mp="1si4dt41pmgvq">
                      <p className="text-sm text-indigo-900 font-medium mb-2" data-id="element-671" data-mp="1sjfjj9eptyph">
                        Cierre:
                      </p>
                      <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-sm text-slate-700 italic" data-id="element-672" data-mp="21ij2mddvue3z">
                        "
                        <strong data-id="element-673" data-mp="26eq9q9i0lvni">
                          ¿Le gustaría probar con este producto en su próximo
                          pedido?
                        </strong>
                        "<br data-id="element-674" data-mp="1nuhlto6cbd5j" />
                        <br data-id="element-675" data-mp="1nt6h9m52m1dd" />
                        <span className="text-xs text-slate-500 not-italic block" data-id="element-676" data-mp="22kgfbdmya0eo">
                          Registra el resultado final de la llamada y guarda.
                        </span>
                      </div>
                    </div>}
                </div>
              </Card>
            </div>

            {/* Right Column: Interactive Workspace */}
            <div className="lg:col-span-2" data-id="element-677" data-mp="14y9f0fxoiu84">
              <Card className="min-h-[500px] flex flex-col" data-id="element-678" data-mp="1jtpc6ttk368q">
                <div className="flex-1" data-id="element-679" data-mp="1jr2u4091h8c9">
                  {/* STEP 1: APERTURA */}
                  {currentStep === 1 && <div className="space-y-6 animate-fade-in" data-id="element-680" data-mp="2v8koi94qcvm4">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-681" data-mp="2v7188ij2qkkc">
                        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center" data-id="element-682" data-mp="2q8znzu9wwf7a">
                          <UserIcon className="w-5 h-5" data-id="element-683" data-mp="2xv3gnbpp3so6" />
                        </div>
                        <div data-id="element-684" data-mp="32teudxj42nbo">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-685" data-mp="2osc7xjo7fb08">
                            1. Apertura de Llamada
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-686" data-mp="2sufrywoxvnx4">
                            Selecciona el cliente a contactar
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4" data-id="element-687" data-mp="3lkph02ht0dxr">
                        <SearchableSelect label="Buscar Cliente / Farmacia" options={clients.map(c => ({
                    value: c.id,
                    label: `${c.nombre} - ${c.zona}`
                  }))} value={selectedClientId} onChange={setSelectedClientId} placeholder="Escribe para buscar..." data-id="element-688" data-mp="37rztftmmn0yj" />
                    

                        {selectedClient && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3" data-id="element-689" data-mp="85t7x2ooiopm">
                            <div className="flex items-start justify-between" data-id="element-690" data-mp="hxcl44lm9l7e">
                              <div data-id="element-691" data-mp="dyvahb2zlsl8">
                                <h4 className="font-bold text-slate-900 text-lg" data-id="element-692" data-mp="8yqtljy62vuh">
                                  {selectedClient.nombre}
                                </h4>
                                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1" data-id="element-693" data-mp="420ht8teamev">
                                  <MapPinIcon className="w-4 h-4" data-id="element-694" data-mp="3v81u1kp3jm23" />{' '}
                                  {selectedClient.zona} |{' '}
                                  {selectedClient.ciudad}
                                </p>
                              </div>
                              <Badge variant="info" data-id="element-695" data-mp="3hg3otkyiaab7">
                                {selectedClient.codigo}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200" data-id="element-696" data-mp="37gcvvnplg3d8">
                              <div data-id="element-697" data-mp="2xdhegv1jupck">
                                <p className="text-xs text-slate-500" data-id="element-698" data-mp="2xcg1um4vxusq">
                                  Teléfono
                                </p>
                                <p className="font-medium text-slate-900" data-id="element-699" data-mp="37ac5ytzenphc">
                                  {selectedClient.telefono || 'No registrado'}
                                </p>
                              </div>
                              <div data-id="element-700" data-mp="2enj3a62ggg4p">
                                <p className="text-xs text-slate-500" data-id="element-701" data-mp="2ekde6qttte8z">
                                  Contacto
                                </p>
                                <p className="font-medium text-slate-900" data-id="element-702" data-mp="2ojcpyi9w1q07">
                                  {selectedClient.contacto || 'No registrado'}
                                </p>
                              </div>
                            </div>
                          </div>}
                      </div>

                      {selectedClient && <div className="pt-6 grid grid-cols-3 gap-3" data-id="element-703" data-mp="20sysudcg50yr">
                          <Button variant="danger" className="w-full" onClick={() => {
                    setCallResult('no_contesto');
                    setCurrentStep(7);
                  }} data-id="element-704" data-mp="3f21hf5jfbeop">
                      
                            No Contestó
                          </Button>
                          <Button variant="warning" className="w-full" onClick={() => {
                    setCallResult('ocupado');
                    setCurrentStep(7);
                  }} data-id="element-705" data-mp="3jzbi5hc9jcru">
                      
                            Ocupado
                          </Button>
                          <Button variant="primary" className="w-full" onClick={handleNext} data-id="element-706" data-mp="bkt4n03jz50u">
                      
                            Cliente Disponible
                          </Button>
                        </div>}
                    </div>}

                  {/* STEP 2: DETECCIÓN */}
                  {currentStep === 2 && <div className="space-y-6 animate-fade-in" data-id="element-707" data-mp="gkxhmg595hmb">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-708" data-mp="104wgapcp3ktq">
                        <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center" data-id="element-709" data-mp="146rirx6d4493">
                          <SearchIcon className="w-5 h-5" data-id="element-710" data-mp="q66yewksu0dh" />
                        </div>
                        <div data-id="element-711" data-mp="v5jtg4ltqdr5">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-712" data-mp="v4rzszbseq3i">
                            2. Detección de Interés
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-713" data-mp="l8oszg5zq3a8">
                            ¿Qué le interesa más al cliente?
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-714" data-mp="cgwqpsr4e0gq">
                        <button onClick={() => setFlow('ofertas')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'ofertas' ? 'border-indigo-600 bg-indigo-50' : 'border-slate-200 hover:border-indigo-300 hover:bg-slate-50'}`} data-id="element-715" data-mp="3tngq9395jlyh">
                      
                          <TagIcon className={`w-8 h-8 mb-3 ${flow === 'ofertas' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-716" data-mp="3ukyyoofr3ejq" />
                      
                          <h4 className="font-bold text-slate-900" data-id="element-717" data-mp="8ad1h7tsmgui">
                            Productos en Oferta
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-718" data-mp="caojecuru32a">
                            Promociones y descuentos actuales
                          </p>
                        </button>

                        <button onClick={() => setFlow('alta_rotacion')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'alta_rotacion' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'}`} data-id="element-719" data-mp="m8uh0i9jon1c">
                      
                          <TrendingUpIcon className={`w-8 h-8 mb-3 ${flow === 'alta_rotacion' ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-720" data-mp="100itwnrmbfjd" />
                      
                          <h4 className="font-bold text-slate-900" data-id="element-721" data-mp="2pksdpihgxoeb">
                            Alta Rotación
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-722" data-mp="2kmqumxk1lp2u">
                            Lo que más se está vendiendo
                          </p>
                        </button>

                        <button onClick={() => setFlow('vencimientos')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'vencimientos' ? 'border-amber-600 bg-amber-50' : 'border-slate-200 hover:border-amber-300 hover:bg-slate-50'}`} data-id="element-723" data-mp="2blsu4p8tbzp0">
                      
                          <ClockIcon className={`w-8 h-8 mb-3 ${flow === 'vencimientos' ? 'text-amber-600' : 'text-slate-400'}`} data-id="element-724" data-mp="2zgc983s4el2j" />
                      
                          <h4 className="font-bold text-slate-900" data-id="element-725" data-mp="3rxy4u9pctb2d">
                            Evitar Vencimientos
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-726" data-mp="3n5ww2xrea2h3">
                            Opciones con fecha larga
                          </p>
                        </button>

                        <button onClick={() => setFlow('validar_riesgo')} className={`p-5 rounded-xl border-2 text-left transition-all ${flow === 'validar_riesgo' ? 'border-rose-600 bg-rose-50' : 'border-slate-200 hover:border-rose-300 hover:bg-slate-50'}`} data-id="element-727" data-mp="ew40ey8eaep2">
                      
                          <AlertTriangleIcon className={`w-8 h-8 mb-3 ${flow === 'validar_riesgo' ? 'text-rose-600' : 'text-slate-400'}`} data-id="element-728" data-mp="3ncz2ibhdbv23" />
                      
                          <h4 className="font-bold text-slate-900" data-id="element-729" data-mp="3eck54dr4qpi8">
                            Validar Riesgo
                          </h4>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-730" data-mp="3if720t2nr0ma">
                            Productos del cliente por vencer
                          </p>
                        </button>
                      </div>
                    </div>}

                  {/* STEP 3: VENCIDOS DEL CLIENTE (Only if validar_riesgo) */}
                  {currentStep === 3 && <div className="space-y-6 animate-fade-in" data-id="element-731" data-mp="312gyfuq3rkt1">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-732" data-mp="2x4j5qhx4igkw">
                        <div className="w-10 h-10 bg-rose-100 text-rose-600 rounded-xl flex items-center justify-center" data-id="element-733" data-mp="3avejo8mhbnvc">
                          <AlertTriangleIcon className="w-5 h-5" data-id="element-734" data-mp="362lddgbd47h5" />
                        </div>
                        <div data-id="element-735" data-mp="3kqzijxw9480v">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-736" data-mp="3ftpuomoig4f5">
                            3. Registro de Vencimientos
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-737" data-mp="3tspk3wjukncd">
                            Registra los productos que el cliente menciona
                          </p>
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4" data-id="element-738" data-mp="1zhaq33jt01qt">
                        <div className="relative" data-id="element-739" data-mp="1wv1q9v7cvnxm">
                          <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-740" data-mp="1wu9twi5xpm6a" />
                          <input type="text" placeholder="Buscar producto en inventario..." value={vencidoSearch} onChange={e => setVencidoSearch(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" data-id="element-741" data-mp="20utpvierujqu" />
                      
                        </div>

                        {vencidoSearch && filteredVencidoSearch.length > 0 && <div className="bg-white border border-slate-200 rounded-lg shadow-sm max-h-48 overflow-y-auto" data-id="element-742" data-mp="1i42lywdjzlju">
                            {filteredVencidoSearch.map(p => <div key={p.id} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-id="element-743" data-mp="1rx05n5ua7gk1">
                        
                                <div data-id="element-744" data-mp="zf48qg6jywz7">
                                  <p className="font-medium text-sm text-slate-900" data-id="element-745" data-mp="ze300id0emkn">
                                    {p.nombre}
                                  </p>
                                  <p className="text-xs text-slate-500" data-id="element-746" data-mp="mws7k472uok4">
                                    SKU: {p.sku}
                                  </p>
                                </div>
                                <Button size="sm" variant="secondary" onClick={() => {
                        setVencidoSearch(p.nombre);
                        // We don't add immediately, let them set qty/notes
                      }} data-id="element-747" data-mp="czngbpydk2wg">
                          
                                  Seleccionar
                                </Button>
                              </div>)}
                          </div>}

                        <div className="grid grid-cols-3 gap-3" data-id="element-748" data-mp="3qf39od4mgsls">
                          <div className="col-span-1" data-id="element-749" data-mp="3lf762ezu736r">
                            <Input type="number" label="Cantidad" value={vencidoQty} onChange={e => setVencidoQty(Number(e.target.value))} min={1} data-id="element-750" data-mp="3lgs2691suu6q" />
                        
                          </div>
                          <div className="col-span-2" data-id="element-751" data-mp="36u366h6raz53">
                            <Input label="Notas (Lote, Fecha aprox)" value={vencidoNotes} onChange={e => setVencidoNotes(e.target.value)} placeholder="Ej: Vence en 2 meses" data-id="element-752" data-mp="3bs4lqs4z4gbt" />
                        
                          </div>
                        </div>

                        <Button className="w-full" disabled={!vencidoSearch} onClick={() => {
                    const prod = inventory.find(p => p.nombre === vencidoSearch || p.sku === vencidoSearch);
                    if (prod) {
                      addVencidoCliente(prod);
                    } else {
                      // Allow adding custom text if not in inventory
                      setVencidosCliente(prev => [...prev, {
                        productoId: 'custom',
                        nombre: vencidoSearch,
                        sku: 'N/A',
                        cantidad: vencidoQty,
                        notas: vencidoNotes
                      }]);
                      setVencidoSearch('');
                      setVencidoQty(1);
                      setVencidoNotes('');
                    }
                  }} data-id="element-753" data-mp="3qfy0kam8jetc">
                      
                          <PlusIcon className="w-4 h-4 mr-2" data-id="element-754" data-mp="116lwrkwpg4h8" /> Agregar a la
                          lista
                        </Button>
                      </div>

                      {vencidosCliente.length > 0 && <div className="space-y-2" data-id="element-755" data-mp="8hx4mbdmn9hm">
                          <h4 className="text-sm font-bold text-slate-700" data-id="element-756" data-mp="4fjy3kr237js">
                            Productos Registrados:
                          </h4>
                          {vencidosCliente.map((v, i) => <div key={i} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200" data-id="element-757" data-mp="dex9iiyqq675" data-mpi={i}>
                      
                              <div data-id="element-758" data-mp="1tv9pg1js9w4t">
                                <p className="font-medium text-sm text-slate-900" data-id="element-759" data-mp="1ov55g1jo2j9u">
                                  {v.nombre}{' '}
                                  <span className="text-slate-500 font-normal" data-id="element-760" data-mp="3jc545g9l5hom">
                                    x{v.cantidad}
                                  </span>
                                </p>
                                {v.notas && <p className="text-xs text-slate-500" data-id="element-761" data-mp="3s95s5lsqtdrj">
                                    {v.notas}
                                  </p>}
                              </div>
                              <button onClick={() => removeVencidoCliente(i)} className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-lg" data-id="element-762" data-mp="5ve68r22jg74">
                        
                                <TrashIcon className="w-4 h-4" data-id="element-763" data-mp="exf7s04c4oel" />
                              </button>
                            </div>)}
                        </div>}
                    </div>}

                  {/* STEP 4: MOSTRAR PRODUCTOS */}
                  {currentStep === 4 && <div className="space-y-6 animate-fade-in" data-id="element-764" data-mp="2lmtnynxhlrbk">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-765" data-mp="2ln23yoowpcqz">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center" data-id="element-766" data-mp="2e4lsr81amv8g">
                          <PackageIcon className="w-5 h-5" data-id="element-767" data-mp="24dhfqdv6hkg9" />
                        </div>
                        <div data-id="element-768" data-mp="24b4zc552ji5b">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-769" data-mp="1zewov51tgd4h">
                            4. Selección de Productos
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-770" data-mp="2x02fa1lt52uw">
                            Elige los productos a ofrecer al cliente
                          </p>
                        </div>
                      </div>

                      <div className="relative" data-id="element-771" data-mp="2j2du0sntvtpb">
                        <SearchIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-772" data-mp="2j1udngdttdde" />
                        <input type="text" placeholder="Buscar en inventario..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none" data-id="element-773" data-mp="1bq5614a4974d" />
                    
                      </div>

                      <div className="bg-slate-50 rounded-xl border border-slate-200 h-[300px] overflow-y-auto p-2" data-id="element-774" data-mp="r2swnpevzm2p">
                        {isLoadingInventory ? <div className="flex items-center justify-center h-full" data-id="element-775" data-mp="m3g7gy0jm2wy">
                            <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-776" data-mp="h5ws1us3zk3f" />
                          </div> : filteredInventory.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-slate-400" data-id="element-777" data-mp="7esdgw6ci194">
                            <PackageIcon className="w-8 h-8 mb-2 opacity-50" data-id="element-778" data-mp="3e03g0pnfsbs" />
                            <p className="text-sm" data-id="element-779" data-mp="3ujrr8tfyesvf">
                              No se encontraron productos
                            </p>
                          </div> : <div className="space-y-2" data-id="element-780" data-mp="3plzpl7pibse1">
                            {filteredInventory.map(product => {
                      const isSelected = offeredProducts.some(p => p.productoId === product.id);
                      return <div key={product.id} onClick={() => toggleOfferedProduct(product)} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-300'}`} data-id="element-781" data-mp="29zw5cnxzqdks">
                            
                                  <div className="flex-1 min-w-0 pr-4" data-id="element-782" data-mp="1razkfaohgxf8">
                                    <p className={`font-medium text-sm truncate ${isSelected ? 'text-indigo-900' : 'text-slate-900'}`} data-id="element-783" data-mp="1r95120sv1dd9">
                                
                                      {product.nombre}
                                    </p>
                                    <div className="flex items-center gap-3 mt-1" data-id="element-784" data-mp="13kvhl5j4d1pa">
                                      <span className="text-xs text-slate-500" data-id="element-785" data-mp="17mgpecgsqpsm">
                                        SKU: {product.sku}
                                      </span>
                                      <span className="text-xs font-medium text-emerald-600" data-id="element-786" data-mp="25zkydtofghtd">
                                        RD$ {product.precio}
                                      </span>
                                      <span className={`text-xs ${product.stock > product.stockMinimo ? 'text-slate-500' : 'text-rose-500'}`} data-id="element-787" data-mp="2foclnoijmdxy">
                                  
                                        Stock: {product.stock}
                                      </span>
                                    </div>
                                  </div>
                                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300'}`} data-id="element-788" data-mp="20xvnce17g45a">
                              
                                    {isSelected && <CheckCircleIcon className="w-3 h-3" data-id="element-789" data-mp="38ebdfro4gp6b" />}
                                  </div>
                                </div>;
                    })}
                          </div>}
                      </div>

                      {offeredProducts.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-790" data-mp="3ktt16w304d1h">
                          {offeredProducts.map(p => <span key={p.productoId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium" data-id="element-791" data-mp="360zfuu11t2ow">
                      
                              {p.nombre}
                              <button onClick={() => setOfferedProducts(prev => prev.filter(x => x.productoId !== p.productoId))} data-id="element-792" data-mp="7hskz66vamax">
                        
                                <XIcon className="w-3 h-3 hover:text-indigo-900" data-id="element-793" data-mp="3ovo94jfftoo8" />
                              </button>
                            </span>)}
                        </div>}
                    </div>}

                  {/* STEP 5: INTERÉS DEL CLIENTE */}
                  {currentStep === 5 && <div className="space-y-6 animate-fade-in" data-id="element-794" data-mp="q9bqbjod6cg4">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-795" data-mp="2yp39aaggd8xt">
                        <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center" data-id="element-796" data-mp="2tpgwg24jdupj">
                          <CheckCircleIcon className="w-5 h-5" data-id="element-797" data-mp="2orf5kv0vcr3i" />
                        </div>
                        <div data-id="element-798" data-mp="2jsucdfk1b7ok">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-799" data-mp="2jtndo10854zb">
                            5. Interés del Cliente
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-800" data-mp="3ch9fp4tt01k2">
                            ¿Cuáles productos le interesaron?
                          </p>
                        </div>
                      </div>

                      {offeredProducts.length === 0 ? <div className="text-center py-8 text-slate-500" data-id="element-801" data-mp="32okgp9ffwg5m">
                          No se ofrecieron productos en el paso anterior.
                        </div> : <div className="space-y-3" data-id="element-802" data-mp="3ve12vpcgv0te">
                          {offeredProducts.map(product => {
                    const isInterested = interestedProducts.some(p => p.productoId === product.productoId);
                    return <div key={product.productoId} onClick={() => toggleInterestedProduct(product)} className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${isInterested ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-300'}`} data-id="element-803" data-mp="bk7ent8sgmnb">
                          
                                <div data-id="element-804" data-mp="uis0b5rwuztk">
                                  <p className={`font-bold ${isInterested ? 'text-amber-900' : 'text-slate-900'}`} data-id="element-805" data-mp="uhqf48h6kwy4">
                              
                                    {product.nombre}
                                  </p>
                                  <p className="text-sm text-slate-500" data-id="element-806" data-mp="14apdgi2hc1ux">
                                    RD$ {product.precio}
                                  </p>
                                </div>
                                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center ${isInterested ? 'bg-amber-500 border-amber-500 text-white' : 'border-slate-300'}`} data-id="element-807" data-mp="1e4emhkm7oycy">
                            
                                  {isInterested && <CheckCircleIcon className="w-4 h-4" data-id="element-808" data-mp="y8oojjbgk4jj" />}
                                </div>
                              </div>;
                  })}
                        </div>}
                    </div>}

                  {/* STEP 6: AJUSTE */}
                  {currentStep === 6 && <div className="space-y-6 animate-fade-in" data-id="element-809" data-mp="12aa7nmel5f26">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-810" data-mp="12c4k0cbsjyun">
                        <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center" data-id="element-811" data-mp="1h6j3edz9pa9u">
                          <TrendingUpIcon className="w-5 h-5" data-id="element-812" data-mp="3iynuxocuu5k1" />
                        </div>
                        <div data-id="element-813" data-mp="3j0ieay8h9pm0">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-814" data-mp="3e049rq634xsq">
                            6. Ajuste de Oferta
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-815" data-mp="1tjmvb7bn7hwe">
                            Si el cliente dudó, ¿qué busca?
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3" data-id="element-816" data-mp="1jpw8s7z2thjc">
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('economico');
                    setCurrentStep(4);
                  }} data-id="element-817" data-mp="1op8weuxrusmz">
                      
                          <DollarSignIcon className="w-4 h-4 mr-3 text-emerald-600" data-id="element-818" data-mp="1eqb2fchku6wl" />{' '}
                          Buscar opciones más económicas
                        </Button>
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('ganancia');
                    setCurrentStep(4);
                  }} data-id="element-819" data-mp="15s7n4xx6q6d8">
                      
                          <TrendingUpIcon className="w-4 h-4 mr-3 text-blue-600" data-id="element-820" data-mp="1myvzk0osyd3u" />{' '}
                          Buscar opciones con mayor margen
                        </Button>
                        <Button variant="secondary" className="justify-start h-12" onClick={() => {
                    setSearchQuery('rotacion');
                    setCurrentStep(4);
                  }} data-id="element-821" data-mp="1izwlnql3f609">
                      
                          <ClockIcon className="w-4 h-4 mr-3 text-amber-600" data-id="element-822" data-mp="1495vemz5gez9" />{' '}
                          Buscar opciones de alta rotación
                        </Button>
                      </div>

                      <div className="mt-8 p-4 bg-slate-50 rounded-xl border border-slate-200 text-center" data-id="element-823" data-mp="v76avkoxesrk">
                        <p className="text-sm text-slate-600 mb-3" data-id="element-824" data-mp="q9wpcg4nr190">
                          Si el cliente ya está convencido o no desea más
                          opciones:
                        </p>
                        <Button onClick={handleNext} className="w-full" data-id="element-825" data-mp="2knxo7107jo93">
                          Ir al Cierre
                        </Button>
                      </div>
                    </div>}

                  {/* STEP 7: CIERRE Y REGISTRO */}
                  {currentStep === 7 && <div className="space-y-6 animate-fade-in" data-id="element-826" data-mp="31wgldxwiyjgg">
                      <div className="flex items-center gap-3 border-b border-slate-100 pb-4" data-id="element-827" data-mp="31yba7nax2vpt">
                        <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center" data-id="element-828" data-mp="36xe4qdt9tjjj">
                          <SaveIcon className="w-5 h-5" data-id="element-829" data-mp="2e6dyrxbdq5p8" />
                        </div>
                        <div data-id="element-830" data-mp="2j5ql8gyc9ara">
                          <h3 className="text-lg font-bold text-slate-900" data-id="element-831" data-mp="2j5h5dm70cjzb">
                            7. Cierre y Registro
                          </h3>
                          <p className="text-sm text-slate-500" data-id="element-832" data-mp="2n57ullgvys1q">
                            Guarda el resultado de la llamada
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4" data-id="element-833" data-mp="tiwbw8onrirq">
                        <div data-id="element-834" data-mp="tk7g29m0msjd">
                          <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-835" data-mp="tkzahh3vmk82">
                            Resultado de la Llamada *
                          </label>
                          <div className="grid grid-cols-2 gap-3" data-id="element-836" data-mp="jstabk3h0whs">
                            <button onClick={() => setCallResult('venta_realizada')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'venta_realizada' ? 'bg-emerald-50 border-emerald-500 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'}`} data-id="element-837" data-mp="or4kjvxoh90j">
                          
                              Venta Realizada
                            </button>
                            <button onClick={() => setCallResult('promesa_compra')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'promesa_compra' ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'}`} data-id="element-838" data-mp="5tk7dm49zyvr">
                          
                              Promesa de Compra
                            </button>
                            <button onClick={() => setCallResult('promesa_cobro')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'promesa_cobro' ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'}`} data-id="element-839" data-mp="3fog17ynujpax">
                          
                              Promesa de Cobro
                            </button>
                            <button onClick={() => setCallResult('seguimiento')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'seguimiento' ? 'bg-amber-50 border-amber-500 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300'}`} data-id="element-840" data-mp="h8esbh6zzuog">
                          
                              Requiere Seguimiento
                            </button>
                            <button onClick={() => setCallResult('no_interesado')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'no_interesado' ? 'bg-rose-50 border-rose-500 text-rose-700' : 'bg-white border-slate-200 text-slate-600 hover:border-rose-300'}`} data-id="element-841" data-mp="hykcchqw6z87">
                          
                              No Interesado
                            </button>
                            <button onClick={() => setCallResult('no_contesto')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'no_contesto' ? 'bg-slate-100 border-slate-400 text-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-842" data-mp="10vlkgbfqtfgd">
                          
                              No Contestó
                            </button>
                            <button onClick={() => setCallResult('ocupado')} className={`p-3 rounded-xl border text-sm font-medium transition-colors ${callResult === 'ocupado' ? 'bg-slate-100 border-slate-400 text-slate-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-843" data-mp="1joh3kbkb8z2y">
                          
                              Ocupado
                            </button>
                          </div>
                        </div>

                        <Textarea label="Notas Adicionales" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Comentarios sobre la llamada, objeciones, o detalles para el seguimiento..." rows={4} data-id="element-844" data-mp="1kqwjg0b69g1e" />
                    

                        {/* Summary Box */}
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm" data-id="element-845" data-mp="27quety0icgju">
                          <h4 className="font-bold text-slate-900 mb-2" data-id="element-846" data-mp="23p8szn5zwrxi">
                            Resumen a Guardar:
                          </h4>
                          <ul className="space-y-1 text-slate-600" data-id="element-847" data-mp="307yaoxbvea70">
                            <li data-id="element-848" data-mp="2vfwwh5v569ec">
                              • <strong data-id="element-849" data-mp="2vevjgvukfctz">Cliente:</strong>{' '}
                              {selectedClient?.nombre}
                            </li>
                            <li data-id="element-850" data-mp="2kx8giiq3qovl">
                              • <strong data-id="element-851" data-mp="2kyb52jzycf8c">Flujo:</strong> {flow || 'N/A'}
                            </li>
                            <li data-id="element-852" data-mp="26z20khl3z9k7">
                              • <strong data-id="element-853" data-mp="26ztiwqpp3asj">Ofrecidos:</strong>{' '}
                              {offeredProducts.length} productos
                            </li>
                            <li data-id="element-854" data-mp="2ypy5mw0xg9wx">
                              • <strong data-id="element-855" data-mp="2yqzo3lk9vyoo">Interesaron:</strong>{' '}
                              {interestedProducts.length} productos
                            </li>
                            <li data-id="element-856" data-mp="2pvtgch1fz6ji">
                              • <strong data-id="element-857" data-mp="2puia8akixmp8">Vencimientos reportados:</strong>{' '}
                              {vencidosCliente.length}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>}
                </div>

                {/* Navigation Footer */}
                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center justify-between" data-id="element-858" data-mp="1az08kgr9wf9f">
                  <Button variant="secondary" onClick={handleBack} disabled={currentStep === 1 || isSubmitting} data-id="element-859" data-mp="16177210x09fh">
                  
                    <ChevronLeftIcon className="w-4 h-4 mr-1" data-id="element-860" data-mp="1kosajw1pcanx" /> Atrás
                  </Button>

                  {currentStep < 7 ? <Button onClick={handleNext} disabled={currentStep === 1 && !selectedClient || currentStep === 2 && !flow} data-id="element-861" data-mp="1fsbkgwaxzm87">
                  
                      Siguiente <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-862" data-mp="n2mj2hublk62" />
                    </Button> : <Button onClick={handleSaveCall} disabled={!callResult || isSubmitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-863" data-mp="11zd7xx6dbdhf">
                  
                      {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" data-id="element-864" data-mp="2ddz8astc0w84" /> : <SaveIcon className="w-4 h-4 mr-2" data-id="element-865" data-mp="28f61tkcgq5hg" />}
                      Guardar Llamada
                    </Button>}
                </div>
              </Card>
            </div>
          </div>
        </>}

      {/* Detail Modal */}
      <Modal isOpen={!!selectedCall} onClose={() => setSelectedCall(null)} title="Detalle de Llamada" size="lg" data-id="element-866" data-mp="2kg5mws5puxlc">
        
        {selectedCall && <ModalBody data-id="element-867" data-mp="2pefmdeyh4kcr">
            <div className="space-y-6" data-id="element-868" data-mp="2uf39vqnhuj28">
              {/* Client & Call Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-869" data-mp="2ud0ndgljv829">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-870" data-mp="1wt5wg7nz13x5">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-871" data-mp="21rh9ssdh5wk4">Cliente</p>
                  <p className="font-bold text-slate-900 text-lg" data-id="element-872" data-mp="26p939qtrpqqf">
                    {selectedCall.cliente_nombre}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-sm text-slate-500" data-id="element-873" data-mp="2bi278chewv1g">
                    <MapPinIcon className="w-3.5 h-3.5" data-id="element-874" data-mp="1e0bqg38bsm52" />
                    {selectedCall.cliente_zona}
                  </div>
                  {selectedCall.cliente_telefono && <div className="flex items-center gap-2 mt-1 text-sm text-slate-500" data-id="element-875" data-mp="5llu5u0woi2">
                      <PhoneIcon className="w-3.5 h-3.5" data-id="element-876" data-mp="3s95pb8l9chek" />
                      {selectedCall.cliente_telefono}
                    </div>}
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200" data-id="element-877" data-mp="3n7yhj9j47i7w">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-878" data-mp="3ia6pmfigzw3p">
                    Información de Llamada
                  </p>
                  <div className="space-y-2" data-id="element-879" data-mp="3ddpydcfz51me">
                    <div className="flex items-center gap-2 text-sm" data-id="element-880" data-mp="38kn2uyrmzi3k">
                      <UserIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-881" data-mp="34jbf6ysbp6y0" />
                      <span className="text-slate-700" data-id="element-882" data-mp="34h7bagufc7xq">
                        {selectedCall.agente_nombre}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm" data-id="element-883" data-mp="2uk2me9971yd8">
                      <CalendarIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-884" data-mp="2pmarr7h5zy5c" />
                      <span className="text-slate-700" data-id="element-885" data-mp="2podkw0dm67ea">
                        {format(new Date(selectedCall.fecha), 'd MMM yyyy, h:mm a', {
                      locale: es
                    })}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-2" data-id="element-886" data-mp="8bv3iqji3jyu">
                      <Badge variant="secondary" className="capitalize" data-id="element-887" data-mp="db8228jqifh9">
                        {selectedCall.flujo_seleccionado.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant={selectedCall.resultado === 'venta_realizada' ? 'success' : selectedCall.resultado === 'promesa_compra' || selectedCall.resultado === 'promesa_cobro' ? 'info' : selectedCall.resultado === 'seguimiento' ? 'warning' : 'danger'} data-id="element-888" data-mp="3djtoft7h9gm">
                      
                        {selectedCall.resultado.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>

              {/* Productos Ofrecidos */}
              {selectedCall.productos_ofrecidos?.length > 0 && <div data-id="element-889" data-mp="2x76fwexxdwbc">
                  <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-890" data-mp="2x8hmemir9i65">
                    <PackageIcon className="w-4 h-4 text-indigo-500" data-id="element-891" data-mp="2t7fkfxxges0h" />
                    Productos Ofrecidos (
                    {selectedCall.productos_ofrecidos.length})
                  </h4>
                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-id="element-892" data-mp="327u4yv2lg32x">
                    <table className="w-full text-sm" data-id="element-893" data-mp="3g3frmvlcuas6">
                      <thead className="bg-slate-50 text-xs text-slate-500" data-id="element-894" data-mp="3c2vy01zzppas">
                        <tr data-id="element-895" data-mp="3pvvb5xcav7f6">
                          <th className="px-3 py-2 text-left font-medium" data-id="element-896" data-mp="3pu0puhx49dak">
                            Producto
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-897" data-mp="3kylfw128c1jf">
                            SKU
                          </th>
                          <th className="px-3 py-2 text-right font-medium" data-id="element-898" data-mp="3uv7yej732gia">
                            Precio
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100" data-id="element-899" data-mp="69h1us7mcccm">
                        {selectedCall.productos_ofrecidos.map((p: any, i: number) => <tr key={i} className="hover:bg-slate-50" data-id="element-900" data-mp="k7xbos0s2iyz" data-mpi={i}>
                              <td className="px-3 py-2 font-medium text-slate-900" data-id="element-901" data-mp="g7wvp2y15zpt">
                                {p.nombre}
                              </td>
                              <td className="px-3 py-2 text-slate-500" data-id="element-902" data-mp="3jnkhfsqsp8kk">
                                {p.sku}
                              </td>
                              <td className="px-3 py-2 text-right text-emerald-600 font-medium" data-id="element-903" data-mp="1ae9o8cqm0m2">
                                RD$ {p.precio}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {/* Productos de Interés */}
              {selectedCall.productos_interes?.length > 0 && <div data-id="element-904" data-mp="2qsdyb9ju22nd">
                  <h4 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-905" data-mp="2qruroozmv0on">
                    <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-906" data-mp="2vl7480r1fh11" />
                    Productos de Interés (
                    {selectedCall.productos_interes.length})
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-907" data-mp="2lucicxhpejg4">
                    {selectedCall.productos_interes.map((p: any, i: number) => <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium border border-emerald-200" data-id="element-908" data-mp="39ht0567cevst" data-mpi={i}>
                  
                        {p.nombre} — RD$ {p.precio}
                      </span>)}
                  </div>
                </div>}

              {/* Productos Vencidos del Cliente */}
              {selectedCall.productos_vencidos_cliente?.length > 0 && <div data-id="element-909" data-mp="1t5x4ieab676d">
                  <h4 className="text-sm font-bold text-rose-700 mb-2 flex items-center gap-2" data-id="element-910" data-mp="1t66tpznj4ab0">
                    <AlertTriangleIcon className="w-4 h-4 text-rose-500" data-id="element-911" data-mp="3vr15qwnufvup" />
                    Productos Vencidos Reportados (
                    {selectedCall.productos_vencidos_cliente.length})
                  </h4>
                  <div className="bg-rose-50 border border-rose-200 rounded-xl overflow-hidden" data-id="element-912" data-mp="3m0ohougtlkjt">
                    <table className="w-full text-sm" data-id="element-913" data-mp="3h2n0kf2yfvaz">
                      <thead className="bg-rose-100 text-xs text-rose-700" data-id="element-914" data-mp="3c4l9b6vdkpof">
                        <tr data-id="element-915" data-mp="382htk61oayxc">
                          <th className="px-3 py-2 text-left font-medium" data-id="element-916" data-mp="3828e3ce98a5w">
                            Producto
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-917" data-mp="333ndhbv3bogx">
                            SKU
                          </th>
                          <th className="px-3 py-2 text-center font-medium" data-id="element-918" data-mp="11bs4j407xy2e">
                            Cantidad
                          </th>
                          <th className="px-3 py-2 text-left font-medium" data-id="element-919" data-mp="wdgw8xpknvma">
                            Notas
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-rose-100" data-id="element-920" data-mp="18typcvyvk6ep">
                        {selectedCall.productos_vencidos_cliente.map((v: any, i: number) => <tr key={i} data-id="element-921" data-mp="1dom628nmzwiq" data-mpi={i}>
                              <td className="px-3 py-2 font-medium text-slate-900" data-id="element-922" data-mp="1ikb76gfb1lh5">
                                {v.nombre}
                              </td>
                              <td className="px-3 py-2 text-slate-500" data-id="element-923" data-mp="pwp55clq6owe">
                                {v.sku}
                              </td>
                              <td className="px-3 py-2 text-center font-bold text-rose-700" data-id="element-924" data-mp="uuz0bn7g9pi0">
                                {v.cantidad}
                              </td>
                              <td className="px-3 py-2 text-slate-600 text-xs" data-id="element-925" data-mp="26bgo6ywzejt1">
                                {v.notas || '—'}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {/* Notas */}
              {selectedCall.notas && <div data-id="element-926" data-mp="24vkxvii6wj4c">
                  <h4 className="text-sm font-bold text-slate-700 mb-2" data-id="element-927" data-mp="24w4ij6z85lm2">
                    Notas
                  </h4>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap" data-id="element-928" data-mp="2dupz4wl4yqk7">
                    {selectedCall.notas}
                  </div>
                </div>}
            </div>
          </ModalBody>}
      </Modal>
    </div>;
}